import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { DEFAULT_FETCH_ERROR_MESSAGE, DEFAULT_UPDATE_ERROR_MESSAGE, getApiErrorMessage } from '@/utils/apiError';
import {
  getRezeptInit,
  getRezeptList,
  getRezeptMassagers,
  kakuteiInit,
  kakuteiExecute,
  getLongTermPatients,
  getGaijiListPdfHtml,
  resetRezeptPrint,
  saveAddTreatmentReport,
} from '@/services/main/rezept/rezeptListService';
import type { MassagerOption, RezeptInitResponse, RezeptListItem } from '@/types/rezept.types';

export function useRezeptInit() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  // serviceType (A2/R2) is sent via the X-Service-Type header (axios interceptor) and
  // controls which 用紙 templates init returns. Must be in the cache key, otherwise
  // switching tabs reuses the other service's init (A2 showing R-2, 用紙 filter mismatch). (HIT-4288)
  const serviceType =
    typeof window !== 'undefined' ? localStorage.getItem('service_type') : null;

  const { data, isLoading, isError, isPending, refetch, error } = useQuery<RezeptInitResponse>({
    queryKey: ['rezept', 'init', tenant, clinicNumber, serviceType],
    queryFn: () => getRezeptInit(tenant ?? '', clinicNumber ?? ''),
    enabled: !!tenant && !!clinicNumber,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
}

export function useRezeptList(params: Record<string, unknown> | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  // serviceType (A2/R2) controls which table BE queries (rezepts vs r_rezepts)
  // via the X-Service-Type header. The cache key must include it so switching
  // tabs doesn't return data from the other tab.
  const serviceType =
    typeof window !== 'undefined' ? localStorage.getItem('service_type') : null;

  const { data, isLoading, isError, refetch, error } = useQuery<{
    rezepts: RezeptListItem[];
    print_num: number;
    is_attachement_document: boolean;
    is_visit_report_document: boolean;
  }>({
    queryKey: ['rezept', 'list', tenant, clinicNumber, serviceType, params],
    queryFn: () => getRezeptList(tenant ?? '', clinicNumber ?? '', params ?? {}),
    enabled: !!tenant && !!clinicNumber && params !== null,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, refetch };
}

export function useSearchRezeptMassagers() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: searchMassagersMutation, isPending: isLoading } = useMutation<
    MassagerOption[],
    Error,
    string
  >({
    mutationFn: searchYearmonth =>
      getRezeptMassagers(tenant ?? '', clinicNumber ?? '', searchYearmonth),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });

  return { searchMassagersMutation, isLoading };
}

export function useLongTermPatients() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ yearmonth, massagerId }: { yearmonth: string; massagerId?: string }) =>
      getLongTermPatients(tenant ?? '', clinicNumber ?? '', yearmonth, massagerId),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });

  return { fetchLongTermPatients: mutateAsync, isLoading: isPending };
}

export function useKakutei() {
  const { tenant, clinicNumber } = useClinicContext();

  const { mutateAsync: init, isPending: isIniting } = useMutation({
    mutationFn: (yearmonth: string) => kakuteiInit(tenant ?? '', clinicNumber ?? '', yearmonth),
  });

  const { mutateAsync: execute, isPending: isExecuting } = useMutation({
    mutationFn: (yearmonth: string) => kakuteiExecute(tenant ?? '', clinicNumber ?? '', yearmonth),
  });

  return { kakuteiInit: init, kakuteiExecute: execute, isIniting, isExecuting };
}

export function useResetRezeptPrint() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (rezeptIds: number[]) =>
      resetRezeptPrint(tenant ?? '', clinicNumber ?? '', rezeptIds),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_UPDATE_ERROR_MESSAGE));
    },
  });

  return { resetPrint: mutateAsync, isResetting: isPending };
}

export function useGaijiListPdf() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (params: Record<string, unknown>) =>
      getGaijiListPdfHtml(tenant ?? '', clinicNumber ?? '', params),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });

  return { fetchGaijiListPdf: mutateAsync, isFetchingGaijiList: isPending };
}

export function useSetAddTreatmentReport() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: ({ id, value }: { id: number; value: 0 | 1 }) =>
      saveAddTreatmentReport(tenant ?? '', clinicNumber ?? '', id, value),
    onSuccess: () => {
      // BD: tick/untick persists immediately to DB → list must refetch to display correctly
      // (checkbox is controlled by row.add_treatment_report; without invalidation
      // it snaps back to the cached value).
      queryClient.invalidateQueries({ queryKey: ['rezept', 'list', tenant, clinicNumber] });
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });

  return { saveAddTreatmentReport: mutateAsync };
}
